import { FormControl, InputLabel, Select, MenuItem, FormHelperText, Autocomplete, Button } from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { DatePicker } from "@mui/x-date-pickers";
import { cpfMask } from "utils/cpf";
import dayjs from "dayjs";

import { addressOptions, locationOptions, stateOptions, countryOptions } from "./AppManualInsert.utils";
import { IBeneficiary, IFieldConfiguration } from "./AppManualInsert.types";
import { TextField } from "./AppManualInsert.styled";
import { formatCNPJ } from "utils/is-valid-cnpj";


interface IProps {
  beneficiary: Partial<IBeneficiary>;
  beneficiaryError: Record<string, string>;
  params: IFieldConfiguration[];
  handleChange: (payload: Partial<IBeneficiary>) => void;
  handleEditSave: () => void;
  handleEditCancel: () => void;
  isCLT: boolean;
}

export function AppManualInsertEdit({ beneficiary, beneficiaryError, handleChange, handleEditSave, handleEditCancel, params, isCLT }: IProps) {
  // console.log("!!!!!!!!", params)
  // const alwaysMandatoryFields = ["name", "legalEntityId", "taxPayerId", "plan", "email"];
  
  // Para CLT: filtra o campo legalEntityId (CNPJ) dos parâmetros
  const filteredParams = isCLT ? params.filter(param => param.key !== 'legalEntityId') : params;
  const alwaysMandatoryFields: string[] = [];
  
  const visibleFields = [...filteredParams.filter(field => !!field.visible).map(field => field.key), ...alwaysMandatoryFields];
  const requiredFields = [...filteredParams.filter(field => !!field.required).map(field => field.key), ...alwaysMandatoryFields];

  // Função para formatar valor monetário
  const formatCurrency = (value: string): string => {
    // Remove tudo que não for número
    const onlyNumbers = value.replace(/\D/g, '');
    
    if (onlyNumbers === '') return '';
    
    // Converte para número e divide por 100 para ter centavos
    const numberValue = parseInt(onlyNumbers, 10) / 100;
    
    // Formatar como moeda brasileira com símbolo R$
    const formatted = numberValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    
    return `R$ ${formatted}`;
  };

  // Função para converter valor formatado de volta para string numérica
  const parseCurrencyToString = (formattedValue: string): string => {
    return formattedValue.replace(/\D/g, '');
  };

  return (
    <>
      <span>Identificação principal</span>
      <div style={{ display: "flex", flexDirection: "row", gap: "24px", flexWrap: "wrap", marginTop: "24px", marginBottom: "50px" }}>
        {visibleFields.includes("name") && (
          <TextField
            label={`Nome completo sem abreviações${requiredFields.includes("name") ? '*' : ''}`}
            value={beneficiary.name || ""}
            onChange={(e) => handleChange({ ...beneficiary, name: e.target.value })}
            error={!!beneficiaryError["name"]}
            helperText={beneficiaryError["name"]}
          />
        )}
        {visibleFields.includes("taxPayerId") && (
          <TextField
            label={`CPF${requiredFields.includes("taxPayerId") ? '*' : ''}`}
            value={beneficiary.taxPayerId ? cpfMask(beneficiary.taxPayerId) : ""}
            onChange={(e) => handleChange({ ...beneficiary, taxPayerId: e.target.value.replace(/\D/g, '').substring(0, 11) })}
            error={!!beneficiaryError["taxPayerId"]}
            helperText={beneficiaryError["taxPayerId"]}
          />
        )}
        {visibleFields.includes("legalEntityId") && (
          <TextField
            label={`CNPJ da empresa do beneficiário${requiredFields.includes("legalEntityId") ? '*' : ''}`}
            value={beneficiary.legalEntityId ? formatCNPJ(beneficiary.legalEntityId, false) : ""}
            onChange={(e) => handleChange({ ...beneficiary, legalEntityId: e.target.value.replace(/\D/g, '').substring(0, 14) })}
            error={!!beneficiaryError["legalEntityId"]}
            helperText={beneficiaryError["legalEntityId"]}
          />
        )}
      </div>
      <span>Dados pessoais</span>
      <div style={{ display: "flex", flexDirection: "row", gap: "24px", flexWrap: "wrap", marginTop: "24px", marginBottom: "50px" }}>
        {visibleFields.includes("email") && (
          <TextField
            label={`Email${requiredFields.includes("email") ? '*' : ''}`}
            value={beneficiary.email || ""}
            onChange={(e) => handleChange({ ...beneficiary, email: e.target.value })}
            error={!!beneficiaryError["email"]}
            helperText={beneficiaryError["email"]}
          />
        )}
        {visibleFields.includes("phoneNumber") && (
          <TextField
            label={`Telefone${requiredFields.includes("phoneNumber") ? '*' : ''}`}
            value={beneficiary.phoneNumber || ""}
            onChange={(e) => handleChange({ ...beneficiary, phoneNumber: e.target.value })}
            error={!!beneficiaryError["phoneNumber"]}
            helperText={beneficiaryError["phoneNumber"]}
          />
        )}
        {visibleFields.includes("race") && (
          <FormControl fullWidth style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!beneficiaryError["race"]}>
            <InputLabel>Raça</InputLabel>
            <Select
              label="Raça"
              value={beneficiary.race || ""}
              onChange={(e) => handleChange({ ...beneficiary, race: e.target.value })}
            >
              <MenuItem value="1 - Branca">Branca</MenuItem>
              <MenuItem value="2 - Preta">Preta</MenuItem>
              <MenuItem value="3 - Parda">Parda</MenuItem>
              <MenuItem value="4 - Amarela">Amarela</MenuItem>
              <MenuItem value="5 - Indígena">Indígena</MenuItem>
              <MenuItem value="6 - Não informado">Não informado</MenuItem>
            </Select>
            {!!beneficiaryError["race"] && <FormHelperText error>{beneficiaryError["race"]}</FormHelperText>}
          </FormControl>
        )}
        {visibleFields.includes("educationLevel") && (
          <FormControl fullWidth style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!beneficiaryError["educationLevel"]}>
            <InputLabel>Nível de escolaridade</InputLabel>
            <Select
              label="Nível de escolaridade"
              value={beneficiary.educationLevel || ""}
              onChange={(e) => handleChange({ ...beneficiary, educationLevel: e.target.value })}
            >
              <MenuItem value="01 - Analfabeto, inclusive o que, embora tenha recebido instrução, não se alfabetizou">Analfabeto, inclusive o que, embora tenha recebido instrução, não se alfabetizou</MenuItem>
              <MenuItem value="02 - Até o 5º ano incompleto do Ensino Fundamental (antiga 4ª série) ou que se tenha alfabetizado sem ter frequentado escola regular">Até o 5º ano incompleto do Ensino Fundamental (antiga 4ª série) ou que se tenha alfabetizado sem ter frequentado escola regular</MenuItem>
              <MenuItem value="03 - 5º ano completo do Ensino Fundamental">5º ano completo do Ensino Fundamental</MenuItem>
              <MenuItem value="04 - Do 6º ao 9º ano do Ensino Fundamental incompleto (antiga 5ª a 8ª série)">Do 6º ao 9º ano do Ensino Fundamental incompleto (antiga 5ª a 8ª série)</MenuItem>
              <MenuItem value="05 - Ensino Fundamental Completo">Ensino Fundamental Completo</MenuItem>
              <MenuItem value="06 - Ensino Médio incompleto">Ensino Médio incompleto</MenuItem>
              <MenuItem value="07 - Ensino Médio completo">Ensino Médio completo</MenuItem>
              <MenuItem value="08 - Educação Superior incompleta">Educação Superior incompleta</MenuItem>
              <MenuItem value="09 - Educação Superior completa">Educação Superior completa</MenuItem>
              <MenuItem value="10 - Pós-Graduação completa">Pós-Graduação completa</MenuItem>
              <MenuItem value="11 - Mestrado completo">Mestrado completo</MenuItem>
              <MenuItem value="12 - Doutorado completo">Doutorado completo</MenuItem>
            </Select>
            {!!beneficiaryError["educationLevel"] && <FormHelperText error>{beneficiaryError["educationLevel"]}</FormHelperText>}
          </FormControl>
        )}
        {visibleFields.includes("nationalityCountry") && (
          <Autocomplete
            disablePortal
            value={{ value: beneficiary.nationalityCountry || "", label: countryOptions.find(co => co.value === beneficiary.nationalityCountry)?.label || "" }}
            onChange={(_, newValue) => handleChange({ ...beneficiary, nationalityCountry: newValue?.value || "" })}
            options={countryOptions.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }))}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }}
            renderInput={(params) => <TextField {...params} label={`País de nacionalidade${requiredFields.includes("nationalityCountry") ? '*' : ''}`} error={!!beneficiaryError["nationalityCountry"]} helperText={beneficiaryError["nationalityCountry"]} />}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
          />
        )}
        {visibleFields.includes("birthCountry") && (
          <Autocomplete
            disablePortal
            value={{ value: beneficiary.birthCountry || "", label: countryOptions.find(co => co.value === beneficiary.birthCountry)?.label || "" }}
            onChange={(_, newValue) => handleChange({ ...beneficiary, birthCountry: newValue?.value || "" })}
            options={countryOptions.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }))}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }}
            renderInput={(params) => <TextField {...params} label={`País de nascimento${requiredFields.includes("birthCountry") ? '*' : ''}`} error={!!beneficiaryError["birthCountry"]} helperText={beneficiaryError["birthCountry"]} />}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
          />
        )}
        {visibleFields.includes("birthState") && (
          <Autocomplete
            disablePortal
            value={{ value: beneficiary.birthState || "", label: beneficiary.birthState || "" }}
            onChange={(_, newValue) => handleChange({ ...beneficiary, birthState: newValue?.value || "" })}
            options={stateOptions.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }))}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }}
            renderInput={(params) => <TextField {...params} label={`Estado de nascimento${requiredFields.includes("birthState") ? '*' : ''}`} error={!!beneficiaryError["birthState"]} helperText={beneficiaryError["birthState"]} />}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
          />
        )}
        {visibleFields.includes("birthCity") && (
          <TextField
            label={`Cidade de nascimento${requiredFields.includes("birthCity") ? '*' : ''}`}
            value={beneficiary.birthCity || ""}
            onChange={(e) => handleChange({ ...beneficiary, birthCity: e.target.value })}
            error={!!beneficiaryError["birthCity"]}
            helperText={beneficiaryError["birthCity"]}
          />
        )}
        {visibleFields.includes("motherName") && (
          <TextField
            label={`Nome completo da mãe sem abreviações${requiredFields.includes("motherName") ? '*' : ''}`}
            value={beneficiary.motherName || ""}
            onChange={(e) => handleChange({ ...beneficiary, motherName: e.target.value })}
            error={!!beneficiaryError["motherName"]}
            helperText={beneficiaryError["motherName"]}
          />
        )}
        {/* <TextField
          label="Nome completo do pai"
          value={beneficiary.fatherName || ""}
          onChange={(e) => handleChange({ ...beneficiary, fatherName: e.target.value })}
          error={!!beneficiaryError["fatherName"]}
          helperText={beneficiaryError["fatherName"]}
        /> */}
        {visibleFields.includes("gender") && (
          <FormControl fullWidth style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!beneficiaryError["gender"]}>
            <InputLabel>{`Gênero${requiredFields.includes("gender") ? '*' : ''}`}</InputLabel>
            <Select
              label={`Gênero${requiredFields.includes("gender") ? '*' : ''}`}
              value={beneficiary.gender || ""}
              onChange={(e) => handleChange({ ...beneficiary, gender: e.target.value })}
            >
              <MenuItem value="M - Masculino">Masculino</MenuItem>
              <MenuItem value="F - Feminino">Feminino</MenuItem>
            </Select>
            {!!beneficiaryError["gender"] && <FormHelperText error>{beneficiaryError["gender"]}</FormHelperText>}
          </FormControl>
        )}
        {visibleFields.includes("birthDate") && (
          <DatePicker
            label={`Data de nascimento${requiredFields.includes("birthDate") ? '*' : ''}`}
            format="DD / MM / YYYY"
            value={beneficiary.birthDate ? dayjs(beneficiary.birthDate) : null}
            onChange={(e) => handleChange({ ...beneficiary, birthDate: e?.format("YYYY-MM-DD") })}
            slotProps={{ textField: { error: !!beneficiaryError["birthDate"], helperText: beneficiaryError["birthDate"], style: { minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" } }}}
          />
        )}
        {visibleFields.includes("maritalStatus") && (
          <FormControl fullWidth style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!beneficiaryError["maritalStatus"]}>
            <InputLabel>{`Estado civil${requiredFields.includes("maritalStatus") ? '*' : ''}`}</InputLabel>
            <Select
              label={`Estado civil${requiredFields.includes("maritalStatus") ? '*' : ''}`}
              value={beneficiary.maritalStatus || ""}
              onChange={(e) => handleChange({ ...beneficiary, maritalStatus: e.target.value })}
            >
              <MenuItem value="1 - Solteiro">Solteiro</MenuItem>
              <MenuItem value="2 - Casado">Casado</MenuItem>
              <MenuItem value="3 - Divorciado">Divorciado</MenuItem>
              <MenuItem value="4 - Separado">Separado</MenuItem>
              <MenuItem value="5 - Viúvo">Viúvo</MenuItem>
            </Select>
            {!!beneficiaryError["maritalStatus"] && <FormHelperText error>{beneficiaryError["maritalStatus"]}</FormHelperText>}
          </FormControl>
        )}
        {visibleFields.includes("weddingDate") && (
          <DatePicker
            label={`Data de casamento${requiredFields.includes("weddingDate") ? '*' : ''}`}
            format="DD / MM / YYYY"
            value={beneficiary.weddingDate ? dayjs(beneficiary.weddingDate) : null}
            onChange={(e) => handleChange({ ...beneficiary, weddingDate: e?.format("YYYY-MM-DD") })}
            slotProps={{ textField: { error: !!beneficiaryError["weddingDate"], helperText: beneficiaryError["weddingDate"], style: { minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" } }}}
          />
        )}
        {visibleFields.includes("nationalIdentityNumber") && (
          <TextField
            label={`RG (apenas números)${requiredFields.includes("nationalIdentityNumber") ? '*' : ''}`}
            value={beneficiary.nationalIdentityNumber || ""}
            onChange={(e) => handleChange({ ...beneficiary, nationalIdentityNumber: e.target.value.replace(/\D/g, '') })}
            error={!!beneficiaryError["nationalIdentityNumber"]}
            helperText={beneficiaryError["nationalIdentityNumber"]}
          />
        )}
        {visibleFields.includes("nationalIdentityEmissor") && (
          <TextField
            label={`Órgão emissor do RG${requiredFields.includes("nationalIdentityEmissor") ? '*' : ''}`}
            value={beneficiary.nationalIdentityEmissor || ""}
            onChange={(e) => handleChange({ ...beneficiary, nationalIdentityEmissor: e.target.value })}
            error={!!beneficiaryError["nationalIdentityEmissor"]}
            helperText={beneficiaryError["nationalIdentityEmissor"]}
          />
        )}
        {visibleFields.includes("nationalIdentityExpeditionDate") && (
          <DatePicker
            label={`Data de expedição do RG${requiredFields.includes("nationalIdentityExpeditionDate") ? '*' : ''}`}
            format="DD / MM / YYYY"
            value={beneficiary.nationalIdentityExpeditionDate ? dayjs(beneficiary.nationalIdentityExpeditionDate) : null}
            onChange={(e) => handleChange({ ...beneficiary, nationalIdentityExpeditionDate: e?.format("YYYY-MM-DD") })}
            slotProps={{ textField: { error: !!beneficiaryError["nationalIdentityExpeditionDate"], helperText: beneficiaryError["nationalIdentityExpeditionDate"], style: { minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" } }}}
          />
        )}
      </div>
      <span>Informações contratuais</span>
      <div style={{ display: "flex", flexDirection: "row", gap: "24px", flexWrap: "wrap", marginTop: "24px", marginBottom: "50px" }}>
        {visibleFields.includes("plan") && (
          <FormControl fullWidth style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!beneficiaryError["plan"]}>
            <InputLabel>{`Plano${requiredFields.includes("plan") ? '*' : ''}`}</InputLabel>
            <Select
              label={`Plano${requiredFields.includes("plan") ? '*' : ''}`}
              value={beneficiary.plan || ""}
              onChange={(e) => handleChange({ ...beneficiary, plan: e.target.value })}
            >
              {(filteredParams.find(p => p.key === 'plan')?.options ?? []).map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
            {!!beneficiaryError["plan"] && <FormHelperText error>{beneficiaryError["plan"]}</FormHelperText>}
          </FormControl>
        )}
        {visibleFields.includes("status") && (
          <FormControl fullWidth style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!beneficiaryError["status"]}>
            <InputLabel>{`Status${requiredFields.includes("status") ? '*' : ''}`}</InputLabel>
            <Select
              label={`Status${requiredFields.includes("status") ? '*' : ''}`}
              value={beneficiary.status || ""}
              onChange={(e) => handleChange({ ...beneficiary, status: e.target.value })}
            >
              <MenuItem value="ATIVO">Ativo</MenuItem>
              <MenuItem value="INATIVO">Inativo</MenuItem>
              <MenuItem value="SUSPENSO">Suspenso</MenuItem>
            </Select>
            {!!beneficiaryError["status"] && <FormHelperText error>{beneficiaryError["status"]}</FormHelperText>}
          </FormControl>
        )}
        {visibleFields.includes("hiringDate") && (
          <DatePicker
            label={`Data de contratação${requiredFields.includes("hiringDate") ? '*' : ''}`}
            format="DD / MM / YYYY"
            value={beneficiary.hiringDate ? dayjs(beneficiary.hiringDate) : null}
            onChange={(e) => handleChange({ ...beneficiary, hiringDate: e?.format("YYYY-MM-DD") })}
            slotProps={{ textField: { error: !!beneficiaryError["hiringDate"], helperText: beneficiaryError["hiringDate"], style: { minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" } }}}
          />
        )}
        {visibleFields.includes("jobTitle") && (() => {
          const jobTitleOptions = filteredParams.find(p => p.key === 'jobTitle')?.options ?? [];
          return jobTitleOptions.length > 0 ? (
            <Autocomplete
              disablePortal
              freeSolo
              value={beneficiary.jobTitle || ""}
              onChange={(_, newValue) => handleChange({ ...beneficiary, jobTitle: newValue || "" })}
              onInputChange={(_, newValue) => handleChange({ ...beneficiary, jobTitle: newValue || "" })}
              options={jobTitleOptions.map(o => o.value)}
              style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Cargo${requiredFields.includes("jobTitle") ? '*' : ''}`}
                  error={!!beneficiaryError["jobTitle"]}
                  helperText={beneficiaryError["jobTitle"]}
                />
              )}
            />
          ) : (
            <TextField
              label={`Cargo${requiredFields.includes("jobTitle") ? '*' : ''}`}
              value={beneficiary.jobTitle || ""}
              onChange={(e) => handleChange({ ...beneficiary, jobTitle: e.target.value })}
              error={!!beneficiaryError["jobTitle"]}
              helperText={beneficiaryError["jobTitle"]}
            />
          );
        })()}
        {visibleFields.includes("cbo") && (
          <TextField
            label={`CBO${requiredFields.includes("cbo") ? '*' : ''}`}
            value={beneficiary.cbo || ""}
            onChange={(e) => handleChange({ ...beneficiary, cbo: e.target.value })}
            error={!!beneficiaryError["cbo"]}
            helperText={beneficiaryError["cbo"]}
          />
        )}
        {visibleFields.includes("registrationNumber") && (
          <TextField
            label={`Matrícula${requiredFields.includes("registrationNumber") ? '*' : ''}`}
            value={beneficiary.registrationNumber || ""}
            onChange={(e) => handleChange({ ...beneficiary, registrationNumber: e.target.value })}
            error={!!beneficiaryError["registrationNumber"]}
            helperText={beneficiaryError["registrationNumber"]}
          />
        )}
        {visibleFields.includes("paymentFrequency") && (
          <FormControl fullWidth style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!beneficiaryError["paymentFrequency"]}>
            <InputLabel>{`Periodicidade de pagamento${requiredFields.includes("paymentFrequency") ? '*' : ''}`}</InputLabel>
            <Select
              label={`Periodicidade de pagamento${requiredFields.includes("paymentFrequency") ? '*' : ''}`}
              value={beneficiary.paymentFrequency || ""}
              onChange={(e) => handleChange({ ...beneficiary, paymentFrequency: e.target.value })}
            >
              <MenuItem value="1 - Por Hora">Por Hora</MenuItem>
              <MenuItem value="2 - Por Dia">Por Dia</MenuItem>
              <MenuItem value="3 - Por Semana">Por Semana</MenuItem>
              <MenuItem value="4 - Por Quinzena">Por Quinzena</MenuItem>
              <MenuItem value="5 - Por Mês">Por Mês</MenuItem>
              <MenuItem value="6 - Por Tarefa">Por Tarefa</MenuItem>
              <MenuItem value="7 - Não aplicável">Não aplicável</MenuItem>
            </Select>
            {!!beneficiaryError["paymentFrequency"] && <FormHelperText error>{beneficiaryError["paymentFrequency"]}</FormHelperText>}
          </FormControl>
        )}
        {visibleFields.includes("grossPayment") && (
          <TextField
            label={`Pagamento bruto${requiredFields.includes("grossPayment") ? '*' : ''}`}
            value={formatCurrency(beneficiary.grossPayment || "")}
            onChange={(e) => {
              const rawValue = parseCurrencyToString(e.target.value);
              handleChange({ ...beneficiary, grossPayment: rawValue });
            }}
            error={!!beneficiaryError["grossPayment"]}
            helperText={beneficiaryError["grossPayment"]}
            placeholder="R$ 0,00"
          />
        )}
      </div>
      <span>Endereço</span>
      <div style={{ display: "flex", flexDirection: "row", gap: "24px", flexWrap: "wrap", marginTop: "24px" }}>
        {visibleFields.includes("zipCode") && (
          <TextField
            label={`CEP${requiredFields.includes("zipCode") ? '*' : ''}`}
            value={beneficiary.zipCode || ""}
            onChange={(e) => handleChange({ ...beneficiary, zipCode: e.target.value.replace(/\D/g, '').substring(0, 8) })}
            error={!!beneficiaryError["zipCode"]}
            helperText={beneficiaryError["zipCode"]}
          />
        )}
        {visibleFields.includes("addressType") && (
          <Autocomplete
            disablePortal
            value={{ value: beneficiary.addressType || "", label: addressOptions.find(ao => ao.value === beneficiary.addressType)?.label || "" }}
            onChange={(_, newValue) => handleChange({ ...beneficiary, addressType: newValue?.value || "" })}
            options={addressOptions.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }))}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }}
            renderInput={(params) => <TextField {...params} label={`Tipo do endereço${requiredFields.includes("addressType") ? '*' : ''}`} error={!!beneficiaryError["addressType"]} helperText={beneficiaryError["addressType"]} />}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
          />
        )}
        {visibleFields.includes("address") && (
          <TextField
            label={`Endereço${requiredFields.includes("address") ? '*' : ''}`}
            value={beneficiary.address || ""}
            onChange={(e) => handleChange({ ...beneficiary, address: e.target.value })}
            error={!!beneficiaryError["address"]}
            helperText={beneficiaryError["address"]}
          />
        )}
        {visibleFields.includes("addressNumber") && (
          <TextField
            label={`Número${requiredFields.includes("addressNumber") ? '*' : ''}`}
            value={beneficiary.addressNumber || ""}
            onChange={(e) => handleChange({ ...beneficiary, addressNumber: e.target.value })}
            error={!!beneficiaryError["addressNumber"]}
            helperText={beneficiaryError["addressNumber"]}
          />
        )}
        {visibleFields.includes("complement") && (
          <TextField
            label={`Complemento${requiredFields.includes("complement") ? '*' : ''}`}
            value={beneficiary.complement || ""}
            onChange={(e) => handleChange({ ...beneficiary, complement: e.target.value })}
            error={!!beneficiaryError["complement"]}
            helperText={beneficiaryError["complement"]}
          />
        )}
        {visibleFields.includes("neighborhood") && (
          <TextField
            label={`Bairro${requiredFields.includes("neighborhood") ? '*' : ''}`}
            value={beneficiary.neighborhood || ""}
            onChange={(e) => handleChange({ ...beneficiary, neighborhood: e.target.value })}
            error={!!beneficiaryError["neighborhood"]}
            helperText={beneficiaryError["neighborhood"]}
          />
        )}
        {visibleFields.includes("state") && (
          <Autocomplete
            disablePortal
            value={{ value: beneficiary.state || "", label: beneficiary.state || "" }}
            onChange={(_, newValue) => handleChange({ ...beneficiary, state: newValue?.value || "", city: "" })}
            options={stateOptions.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }))}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }}
            renderInput={(params) => <TextField {...params} label={`Estado${requiredFields.includes("state") ? '*' : ''}`} error={!!beneficiaryError["state"]} helperText={beneficiaryError["state"]} />}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
          />
        )}
        {visibleFields.includes("city") && (
          <Autocomplete
            disablePortal
            value={{ value: beneficiary.city || "", label: beneficiary.city || "" }}
            onChange={(_, newValue) => handleChange({ ...beneficiary, city: newValue?.value || "" })}
            options={locationOptions.find(row => row.state === beneficiary.state)?.cities.map(row => ({ value: row, label: row }))?.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' })) ?? []}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }}
            renderInput={(params) => <TextField {...params} label={`Cidade${requiredFields.includes("city") ? '*' : ''}`} error={!!beneficiaryError["city"]} helperText={beneficiaryError["city"]} />}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
          />
        )}
        {visibleFields.includes("municipality") && (
          <TextField
            label={`Município${requiredFields.includes("municipality") ? '*' : ''}`}
            value={beneficiary.municipality || ""}
            onChange={(e) => handleChange({ ...beneficiary, municipality: e.target.value })}
            error={!!beneficiaryError["municipality"]}
            helperText={beneficiaryError["municipality"]}
          />
        )}
      </div>
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          gap: "8px",
          marginTop: "50px",
        }}
      >
        <Button
          variant="outlined"
          style={{
            paddingTop: "0.25rem",
            paddingBottom: "0.25rem",
            paddingLeft: "1rem",
            paddingRight: "1rem",
            borderRadius: "0.375rem",
            height: "2.5rem",
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
            fontWeight: 600,
          }}
          startIcon={<ChevronLeftIcon color="primary" style={{ height: "22px", marginLeft: "-8px"}} />}
          onClick={handleEditCancel}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          style={{
            paddingTop: "0.25rem",
            paddingBottom: "0.25rem",
            paddingLeft: "1rem",
            paddingRight: "1rem",
            borderRadius: "0.375rem",
            height: "2.5rem",
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
            fontWeight: 600,
          }}
          onClick={handleEditSave}
        >
          Salvar beneficiário
        </Button>
      </div>
    </>
  );
}
